import React, { useState } from 'react';
import { Info, AlertTriangle } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import ToolPageLayout from '../../components/ToolPageLayout';
import { useLanguage } from '../../contexts/LanguageContext';

const parseDate = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setUTCDate(d.getUTCDate() + days);
  return d;
};

const formatDate = (date, isRTL) =>
  date.toLocaleDateString(isRTL ? 'ar-DZ-u-nu-latn' : 'fr-FR', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

const MaternityLeaveCalculator = () => {
  const { t, isRTL } = useLanguage();
  const [tab, setTab] = useState('maternite');

  // Congé maternité
  const [dueDate, setDueDate] = useState('');
  const [leaveStartOffset, setLeaveStartOffset] = useState('42');

  const due = parseDate(dueDate);
  const offset = parseInt(leaveStartOffset, 10);
  const leaveStart = due ? addDays(due, -offset) : null;
  const leaveEnd = leaveStart ? addDays(leaveStart, 149) : null; // 150 jours au total

  // Arrêt maladie
  const [dailySalary, setDailySalary] = useState('');
  const [sickDays, setSickDays] = useState('');

  const daily = parseFloat(dailySalary);
  const days = parseInt(sickDays, 10);
  const validSick = daily > 0 && days > 0;
  let sickTotal = null;
  if (validSick) {
    const first15 = Math.min(days, 15);
    const rest = Math.max(0, days - 15);
    sickTotal = first15 * daily * 0.5 + rest * daily * 1;
  }

  return (
    <ToolPageLayout
      title={t('maternity_title')}
      subtitle={t('maternity_subtitle')}
      specialty="generaliste"
    >
      <div className="flex gap-3 mb-8">
        <button
          type="button"
          onClick={() => setTab('maternite')}
          className={`flex-1 rounded-xl px-4 py-3 text-sm font-medium border transition-colors ${tab === 'maternite' ? 'bg-[#0596DE] text-white border-[#0596DE]' : 'bg-white text-[#0F172A] border-[#E2E8F0]'}`}
        >
          {t('maternity_tab_leave')}
        </button>
        <button
          type="button"
          onClick={() => setTab('maladie')}
          className={`flex-1 rounded-xl px-4 py-3 text-sm font-medium border transition-colors ${tab === 'maladie' ? 'bg-[#0596DE] text-white border-[#0596DE]' : 'bg-white text-[#0F172A] border-[#E2E8F0]'}`}
        >
          {t('maternity_tab_sick')}
        </button>
      </div>

      {tab === 'maternite' && (
        <div>
          <div className="bg-[#E6F4FB] border border-[#0596DE]/20 rounded-2xl p-4 flex gap-3 mb-6">
            <Info className="w-5 h-5 text-[#0596DE] shrink-0 mt-0.5" />
            <p className="text-sm text-[#0F172A]">
              {t('maternity_info_pre')}<strong>{t('maternity_info_strong1')}</strong>{t('maternity_info_mid')}<strong>{t('maternity_info_strong2')}</strong>{t('maternity_info_post')}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 mb-6">
            <div>
              <Label className="mb-1 block">{t('maternity_due_date_label')}</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="rounded-xl" />
            </div>
            <div>
              <Label className="mb-1 block">{t('maternity_leave_start_label')}</Label>
              <select
                value={leaveStartOffset}
                onChange={(e) => setLeaveStartOffset(e.target.value)}
                className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white h-10 focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
              >
                <option value="42">{t('maternity_start_42')}</option>
                <option value="21">{t('maternity_start_21')}</option>
                <option value="14">{t('maternity_start_14')}</option>
                <option value="0">{t('maternity_start_0')}</option>
              </select>
            </div>
          </div>

          {leaveStart && leaveEnd && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-5">
                <div className="text-sm text-[#64748B] mb-1">{t('maternity_leave_start_result')}</div>
                <div className="text-lg font-semibold text-[#0F172A]">{formatDate(leaveStart, isRTL)}</div>
              </div>
              <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-5">
                <div className="text-sm text-[#64748B] mb-1">{t('maternity_leave_end_result')}</div>
                <div className="text-lg font-semibold text-[#0F172A]">{formatDate(leaveEnd, isRTL)}</div>
              </div>
            </div>
          )}

          <p className="mt-6 text-sm text-[#64748B]">
            {t('maternity_extension_note')}
          </p>
        </div>
      )}

      {tab === 'maladie' && (
        <div>
          <div className="bg-[#E6F4FB] border border-[#0596DE]/20 rounded-2xl p-4 flex gap-3 mb-6">
            <Info className="w-5 h-5 text-[#0596DE] shrink-0 mt-0.5" />
            <p className="text-sm text-[#0F172A]">
              {t('sick_info_pre')}<strong>{t('sick_info_strong1')}</strong>{t('sick_info_mid')}<strong>{t('sick_info_strong2')}</strong>{t('sick_info_post')}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 mb-6">
            <div>
              <Label className="mb-1 block">{t('sick_daily_salary_label')}</Label>
              <Input type="number" min="0" value={dailySalary} onChange={(e) => setDailySalary(e.target.value)} placeholder="Ex: 2000" className="rounded-xl" />
            </div>
            <div>
              <Label className="mb-1 block">{t('sick_days_label')}</Label>
              <Input type="number" min="1" max="1095" value={sickDays} onChange={(e) => setSickDays(e.target.value)} placeholder={t('sick_days_placeholder')} className="rounded-xl" />
            </div>
          </div>

          {validSick && (
            <div className="bg-[#F0FDF4] border border-[#10B981]/30 rounded-2xl p-6 text-center">
              <p className="text-xs text-[#065F46] mb-1">{t('sick_total_label')}</p>
              <p className="text-2xl font-bold text-[#10B981]">{Math.round(sickTotal).toLocaleString('fr-FR')} DZD</p>
            </div>
          )}

          <p className="mt-6 text-sm text-[#64748B]">
            {t('sick_note')}
          </p>
        </div>
      )}

      <div className="mt-8 bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-2xl p-4 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-[#F59E0B] shrink-0 mt-0.5" />
        <p className="text-sm text-[#92400E]">
          {t('maternity_legal_disclaimer')}
        </p>
      </div>
    </ToolPageLayout>
  );
};

export default MaternityLeaveCalculator;
